import { motion, useReducedMotion } from "framer-motion";
import { useDebateStore } from "../../store/debateStore";
import { selectConsensusGauge } from "../../store/selectors";
import { t } from "../../i18n";

const SOFT = 0.65;
const STRONG = 0.85;

const SIZE = 88;
const STROKE = 7;
const R = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * R;
const ARC_SWEEP = 0.75;
const ARC_LENGTH = CIRCUMFERENCE * ARC_SWEEP;
const ROTATION = 135;

function arcColor(score: number): string {
  if (score >= STRONG) return "hsl(152 60% 50%)";
  if (score >= SOFT) return "hsl(38 90% 55%)";
  return "hsl(215 20% 55%)";
}

function glowColor(score: number): string {
  if (score >= STRONG) return "0 0 8px hsl(152 60% 50% / 0.6)";
  if (score >= SOFT) return "0 0 8px hsl(38 90% 55% / 0.5)";
  return "0 0 6px hsl(215 20% 55% / 0.3)";
}

export function ConsensusMeter() {
  const reduceMotion = useReducedMotion();
  const { scalarScore, pending } = useDebateStore(selectConsensusGauge);
  const pct = Math.max(0, Math.min(100, Math.round(scalarScore * 100)));
  const dashOffset = ARC_LENGTH * (1 - scalarScore);

  return (
    <div
      className="relative flex flex-col items-center"
      role="meter"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct}
      aria-label={t("moderator.consensusLabel")}
    >
      <svg
        width={SIZE}
        height={SIZE}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        className="overflow-visible"
      >
        {/* Background track */}
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={R}
          fill="none"
          stroke="hsl(220 15% 18%)"
          strokeWidth={STROKE}
          strokeDasharray={`${ARC_LENGTH} ${CIRCUMFERENCE}`}
          strokeLinecap="round"
          transform={`rotate(${ROTATION} ${SIZE / 2} ${SIZE / 2})`}
        />

        {/* Threshold markers */}
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={R}
          fill="none"
          stroke="hsl(38 90% 55% / 0.3)"
          strokeWidth={2}
          strokeDasharray={`1 ${CIRCUMFERENCE}`}
          strokeDashoffset={-(ARC_LENGTH * SOFT)}
          transform={`rotate(${ROTATION} ${SIZE / 2} ${SIZE / 2})`}
        />
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={R}
          fill="none"
          stroke="hsl(152 60% 50% / 0.3)"
          strokeWidth={2}
          strokeDasharray={`1 ${CIRCUMFERENCE}`}
          strokeDashoffset={-(ARC_LENGTH * STRONG)}
          transform={`rotate(${ROTATION} ${SIZE / 2} ${SIZE / 2})`}
        />

        {/* Active arc (animated) */}
        <motion.circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={R}
          fill="none"
          stroke={arcColor(scalarScore)}
          strokeWidth={STROKE}
          strokeDasharray={`${ARC_LENGTH} ${CIRCUMFERENCE}`}
          strokeLinecap="round"
          transform={`rotate(${ROTATION} ${SIZE / 2} ${SIZE / 2})`}
          style={{ filter: `drop-shadow(${glowColor(scalarScore)})` }}
          initial={false}
          animate={{ strokeDashoffset: dashOffset }}
          transition={
            reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 100, damping: 22 }
          }
        />
      </svg>

      {/* Center value */}
      <div className="absolute inset-0 flex flex-col items-center justify-center pt-1">
        <span className="text-lg font-bold tabular-nums text-fg-strong leading-none">
          {pending ? "…" : `${pct}`}
        </span>
        <span className="text-[8px] uppercase tracking-widest text-fg-subtle mt-0.5">
          konsensüs
        </span>
      </div>
    </div>
  );
}
